import { logger } from "@/utils/logger";
import i18n from "@/i18n";
import {UON} from "@/utils";
import {useApi} from "@/requests/useApi";
import store from "./store";

export const treatLocale = (value: any) => {
    const raw = typeof value === "object" ? value?.id : value;
    if (!raw || typeof raw !== "string") {
        return { language: "fr", country: "FR" };
    }
    const aValue = raw.split("-");
    return {
        language: aValue[0],
        country: aValue[1],
    };
};

const loadingLocales = new Set<string>();

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === "object" && !Array.isArray(value);

const mergeMessages = (
    base: Record<string, unknown>,
    override: Record<string, unknown>
): Record<string, unknown> => {
    const result: Record<string, unknown> = { ...base };
    for (const key of Object.keys(override)) {
        const overrideVal = override[key];
        if (overrideVal === null || overrideVal === undefined) {
            continue;
        }
        const baseVal = base[key];
        if (isPlainObject(overrideVal) && isPlainObject(baseVal)) {
            result[key] = mergeMessages(baseVal, overrideVal);
        } else {
            result[key] = overrideVal;
        }
    }
    return result;
};

const getStaticMessagesForLocale = (locale: string): Record<string, unknown> => {
    const staticLocale = locale.startsWith("en") ? "en-US" : "fr-FR";
    return (
        (i18n.global.getLocaleMessage(locale) as Record<string, unknown>) ||
        (i18n.global.getLocaleMessage(staticLocale) as Record<string, unknown>) ||
        (i18n.global.getLocaleMessage("fr-FR") as Record<string, unknown>) ||
        {}
    );
};

const ensureLocaleMessages = (locale: string) => {
    const currentMessages = i18n.global.getLocaleMessage(locale) as Record<string, unknown>;
    if (currentMessages && Object.keys(currentMessages).length > 0) {
        return;
    }
    const staticMessages = getStaticMessagesForLocale(locale);
    if (Object.keys(staticMessages).length > 0) {
        i18n.global.setLocaleMessage(locale, staticMessages as any);
    }
};

async function fetchMessagesInBackground(
    locale: string,
    options: { language: string; country?: string }
) {
    try {
        const { response } = await useApi(UON.SystemUid.odmConfiguration, "translation/translate").anonymous(options);
        if (response && typeof response === "object") {
            const baseMessages = getStaticMessagesForLocale(locale);
            i18n.global.setLocaleMessage(
                locale,
                mergeMessages(baseMessages, response as Record<string, unknown>) as any
            );
        } else {
            logger.error(`No settled language for the specific locale ${locale} in database!`);
        }
    } catch (error) {
        logger.error("Error fetching messages:", error);
    } finally {
        loadingLocales.delete(locale);
    }
}

export function loadMessages(options: { language: string; country?: string }) {
    const locale = `${options.language}${options.country ? "-" + options.country : ""}`;

    ensureLocaleMessages(locale);
    i18n.global.locale.value = locale;
    localStorage.setItem("locale", locale);

    if (loadingLocales.has(locale)) {
        return;
    }

    loadingLocales.add(locale);
    void fetchMessagesInBackground(locale, options);
}

export const getTranslations = async (value: any, checkLocal?: boolean) => {
    let msgValue: any;
    if (checkLocal) {
        msgValue = localStorage.getItem("locale") || value;
        const option = store.getters["authModule/getUserConnected"].preferredLanguages.filter(
            (item: any) => item.id === msgValue || item.id === msgValue.id
        );
        store.state.authModule!.user.selectedLanguage = option[0];
    } else {
        msgValue = value;
        store.state.authModule!.user.selectedLanguage = value;
    }

    const { language, country } = treatLocale(msgValue);
    loadMessages({ language, country });
};
